import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import axios from "axios";
import {
  Archive, Download, FileWarning, Layers, Image as ImageIcon, Code2,
  Type, Boxes, ExternalLink, Clock, ShieldAlert, ChevronRight, Globe,
  CircleCheck, Search, Home,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtTs = (ts) => {
  if (!ts || ts.length < 8) return "—";
  return `${ts.slice(0, 4)}-${ts.slice(4, 6)}-${ts.slice(6, 8)}`;
};

function StatPill({ icon: Icon, label, value }) {
  return (
    <div className="stat-pill" data-testid={`stat-${label.toLowerCase()}`}>
      <Icon size={15} strokeWidth={2} />
      <span className="stat-val">{value}</span>
      <span className="stat-lbl">{label}</span>
    </div>
  );
}

export default function RecoveryExplorer() {
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);
  const [active, setActive] = useState("/");
  const [eraFilter, setEraFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("preview");

  useEffect(() => {
    axios.get(`${API}/report`).then((r) => setReport(r.data)).catch((e) => setErr(String(e)));
  }, []);

  const activePage = useMemo(
    () => report?.pages?.find((p) => p.route === active) || null,
    [report, active]
  );

  const filtered = useMemo(() => {
    if (!report) return [];
    return report.pages.filter((p) => {
      if (eraFilter !== "all" && p.era !== eraFilter) return false;
      if (query && !(`${p.title} ${p.route}`.toLowerCase().includes(query.toLowerCase()))) return false;
      return true;
    });
  }, [report, eraFilter, query]);

  if (err) return <div className="boot">Failed to load recovery report.<br /><code>{err}</code></div>;
  if (!report) return <div className="boot"><Archive className="pulse" size={30} /> Loading recovery index…</div>;

  const keyRoutes = new Set(report.key_pages.map((p) => p.route));
  const wp = filtered.filter((p) => p.era === "wordpress");
  const st = filtered.filter((p) => p.era === "static");

  const PageRow = (p) => (
    <button
      key={p.route}
      data-testid={`page-${p.route === "/" ? "home" : p.route.replace(/\//g, "-").replace(/^-|-$/g, "")}`}
      onClick={() => { setActive(p.route); setTab("preview"); }}
      className={`page-row ${active === p.route ? "active" : ""}`}
    >
      <span className="pr-left">
        {keyRoutes.has(p.route) && <span className="key-dot" title="Key page" />}
        <span className="pr-title">{p.title}</span>
      </span>
      <span className="pr-meta">
        <span className="pr-route">{p.route}</span>
        <ChevronRight size={14} />
      </span>
    </button>
  );

  return (
    <div className="shell">
      {/* Top bar */}
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark"><Archive size={20} strokeWidth={2.2} /></div>
          <div>
            <div className="brand-title">WAYBACK RECOVERY</div>
            <a className="brand-sub" href={`${API}/site/`} target="_blank" rel="noreferrer">
              <Globe size={12} /> {report.domain} <ExternalLink size={11} />
            </a>
          </div>
        </div>
        <div className="stats">
          <StatPill icon={Layers} label="Pages" value={report.counts.pages} />
          <StatPill icon={Code2} label="CSS/JS" value={report.counts.css + report.counts.js} />
          <StatPill icon={ImageIcon} label="Images" value={report.counts.images} />
          <StatPill icon={Type} label="Fonts" value={report.counts.fonts} />
          <StatPill icon={Boxes} label="Files" value={report.counts.total_files} />
        </div>
        <a className="dl-btn" data-testid="download-btn" href={`${API}/download`}>
          <Download size={16} /> Download ZIP
        </a>
      </header>

      <div className="body">
        {/* Sidebar */}
        <aside className="sidebar">
          <div className="reco">
            <div className="reco-head"><CircleCheck size={15} /> Recommended target</div>
            <p>{report.recommendation}</p>
          </div>

          <div className="search-box">
            <Search size={14} />
            <input
              data-testid="page-search"
              placeholder="Filter pages…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="era-tabs">
            {["all", "wordpress", "static"].map((e) => (
              <button
                key={e}
                data-testid={`era-${e}`}
                className={eraFilter === e ? "active" : ""}
                onClick={() => setEraFilter(e)}
              >
                {e === "all" ? "All" : e === "wordpress" ? "WordPress" : "Static"}
              </button>
            ))}
          </div>

          <div className="page-list" data-testid="page-list">
            {wp.length > 0 && (
              <>
                <div className="grp-label">WordPress era · 2019–2025</div>
                {wp.map(PageRow)}
              </>
            )}
            {st.length > 0 && (
              <>
                <div className="grp-label">Static era · 2008–2017</div>
                {st.map(PageRow)}
              </>
            )}
            {filtered.length === 0 && <div className="empty">No pages match.</div>}
          </div>
        </aside>

        {/* Main */}
        <main className="main">
          <div className="main-tabs">
            <button data-testid="tab-preview" className={tab === "preview" ? "active" : ""} onClick={() => setTab("preview")}>
              <Home size={14} /> Recovered Preview
            </button>
            <button data-testid="tab-report" className={tab === "report" ? "active" : ""} onClick={() => setTab("report")}>
              <FileWarning size={14} /> Recovery Report
            </button>
          </div>

          {tab === "preview" ? (
            <div className="preview-wrap">
              <div className="fake-chrome">
                <div className="dots"><i /><i /><i /></div>
                <div className="addr">
                  <Archive size={13} />
                  web.archive.org/web/{activePage?.timestamp}/{report.domain}{active}
                </div>
                <div className="snap-date"><Clock size={12} /> {fmtTs(activePage?.timestamp)}</div>
                <a className="open-new" href={`${API}/site${active}`} target="_blank" rel="noreferrer" data-testid="open-new-tab">
                  <ExternalLink size={13} />
                </a>
              </div>
              <iframe
                key={active}
                data-testid="site-frame"
                className="site-frame"
                title="recovered"
                src={`${API}/site${active}`}
              />
            </div>
          ) : (
            <div className="report-wrap" data-testid="report-panel">
              <section className="card">
                <h3><Layers size={16} /> Two eras recovered</h3>
                <div className="era-grid">
                  {Object.entries(report.eras).map(([k, v]) => (
                    <div className={`era-card ${k}`} key={k}>
                      <div className="era-tag">{k}</div>
                      <div className="era-title">{v.label}</div>
                      <div className="era-range">{v.range}</div>
                      <p>{v.note}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="card">
                <h3><ShieldAlert size={16} /> Not recoverable from the archive</h3>
                <ul className="unrec">
                  {report.unrecoverable.categories.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
                {report.unrecoverable.assets_not_crawled.length > 0 && (
                  <div className="not-crawled">
                    <span className="nc-label">Assets never crawled:</span>
                    {report.unrecoverable.assets_not_crawled.map((a) => (
                      <code key={a}>{a.split("/").pop()}</code>
                    ))}
                  </div>
                )}
              </section>

              <section className="card">
                <h3><Clock size={16} /> Source</h3>
                <p className="src-line">
                  Recovered from the <strong>{report.source}</strong>, anchored to snapshot{" "}
                  <code>{fmtTs(report.snapshot_anchor)}</code> (last-good version). Wayback captures
                  public rendered pages, images and CSS only — never databases, PHP backends or admin panels.
                </p>
              </section>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
